/**
 * The graphics switches on the HUD, and the choices remembered from them.
 *
 * Each setting is a row of choices, and each choice a patch over the
 * profile's `Graphics` (config.ts). Most apply at once — the pixel cap, the
 * shadows, the occlusion, the smoothing; the few that shape tiles as they are
 * built (`RELOAD_KEYS`) take a reload, which the game does for them. The
 * choices are kept per browser and laid over the profile on the next visit,
 * unless the URL names an `?fx=` profile, which is taken as asked.
 */

import type { Graphics } from '../config.js';
import { DENSE_GRAPHICS, FULL_GRAPHICS, PHONE_GRAPHICS } from '../config.js';

export interface GraphicsChoice {
  label: string;
  patch: Partial<Graphics>;
}

export interface GraphicsSetting {
  label: string;
  choices: GraphicsChoice[];
}

/** Built into each tile, or read once at start: changing these reloads the game. */
export const RELOAD_KEYS: readonly (keyof Graphics)[] = ['propShadows', 'facetTerrain', 'signScale'];

const onOff = (key: keyof Graphics): GraphicsChoice[] => [
  { label: 'off', patch: { [key]: false } },
  { label: 'on', patch: { [key]: true } },
];

/** The whole profiles, as one row of buttons over the rest. */
export const GRAPHICS_PRESETS: GraphicsChoice[] = [
  { label: 'full', patch: FULL_GRAPHICS },
  { label: 'dense', patch: DENSE_GRAPHICS },
  { label: 'phone', patch: PHONE_GRAPHICS },
];

export const GRAPHICS_SETTINGS: GraphicsSetting[] = [
  { label: 'pixel cap', choices: [1, 1.25, 1.5, 2].map((r) => ({ label: `${r}×`, patch: { maxPixelRatio: r } })) },
  { label: 'shadows', choices: onOff('shadows') },
  { label: 'shadow map', choices: [1024, 2048, 4096].map((n) => ({ label: `${n}`, patch: { shadowMapSize: n } })) },
  { label: 'shadow range', choices: [70, 110, 160].map((m) => ({ label: `${m} m`, patch: { shadowExtent: m } })) },
  {
    label: 'occlusion',
    choices: [
      { label: 'off', patch: { ambientOcclusion: false } },
      { label: 'half', patch: { ambientOcclusion: true, aoScale: 0.5 } },
      { label: 'full', patch: { ambientOcclusion: true, aoScale: 1 } },
    ],
  },
  { label: 'msaa', choices: [0, 2, 4].map((n) => ({ label: n === 0 ? 'off' : `${n}×`, patch: { msaa: n } })) },
  { label: 'fxaa', choices: onOff('fxaa') },
  { label: 'upload', choices: [3, 8, 24].map((mb) => ({ label: `${mb} MB`, patch: { uploadBudget: mb * 1_000_000 } })) },
  { label: 'prop shadows ↻', choices: onOff('propShadows') },
  { label: 'facet terrain ↻', choices: onOff('facetTerrain') },
  { label: 'signs ↻', choices: [0.5, 1].map((s) => ({ label: `${s * 100}%`, patch: { signScale: s } })) },
];

/** Whether `graphics` is what `patch` would make it. */
export function isChosen(graphics: Graphics, patch: Partial<Graphics>): boolean {
  return (Object.keys(patch) as (keyof Graphics)[]).every((key) => graphics[key] === patch[key]);
}

/** Whether going from `from` to `to` takes a reload. */
export function needsReload(from: Graphics, to: Graphics): boolean {
  return RELOAD_KEYS.some((key) => from[key] !== to[key]);
}

const STORAGE_KEY = 'wsg.graphics';

/**
 * The remembered choices, keeping only fields `Graphics` has with the type
 * the profile gives them: storage outlives code, and an old or hand-edited
 * entry should fall back, not break the renderer.
 */
export function loadGraphicsChoices(): Partial<Graphics> {
  let raw: unknown;
  try {
    raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
  if (typeof raw !== 'object' || raw === null) return {};
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (key in FULL_GRAPHICS && typeof value === typeof FULL_GRAPHICS[key as keyof Graphics]) out[key] = value;
  }
  return out as Partial<Graphics>;
}

export function saveGraphicsChoices(choices: Partial<Graphics>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(choices));
  } catch {
    // Not remembered, but applied for this visit.
  }
}

export function clearGraphicsChoices(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing was remembered either.
  }
}
